<template>
  <div style="display: grid; margin-top: 2rem;">
    <button @click="openModal()" class="btn-outline btn-dark"><i class="fa-light fa-arrow-up-from-bracket"></i>Import payments</button>
    
    <!-- validation alert -->
    <alert-modal v-if="isAlertOpen">
      <span><i class="fa-solid fa-circle-check"></i>Bulk allocation complete: : {{ this.countCreated }} new, {{ this.countExisting }} existing</span>
    </alert-modal>

    <!-- modal bulk import -->
    <div class="modal-container" v-if="isModalOpen">
      <div class="modal-content">
        <h1>Bulk Import Payments<i @click="closeModal()" class="fa-regular fa-xmark"></i></h1>
        <span style="font-weight: normal; color: #717182;">Download the CSV template, fill it with payment data, and upload to add payments to the queue.</span>
        <div class="information-container">
          <i class="fa-regular fa-file-lines"></i>
          <div>
            <span>CSV Template</span>
            <span>Download template with sample data and required columns</span>
          </div>
          <button class="download-btn btn btn-outline" @click="exportCSVTemplate"><i class="fa-regular fa-arrow-down-to-bracket"></i>Download Template</button>
        </div>
        <div
          v-if="payments.length == 0"
          class="drop-zone"
          :class="{ hover: isDragOver }"
          @dragover.prevent
          @dragenter.prevent="isDragOver = true"
          @dragleave.prevent="isDragOver = false"
          @drop.prevent="onDrop"
        >
          <i class="fa-regular fa-arrow-up-from-bracket"></i>
          <span>Drag and drop your CSV file here, or click to browse</span>
          <span>CSV file with clientNumber column</span>
          <div>
            <button @click="importCSVFile" type="file" class="btn-outline btn-dark"><i class="fa-regular fa-arrow-up-from-bracket"></i> Select CSV file</button>
          </div>
        </div>
        <div v-else>
          <div style="display: flex; justify-content: space-between; align-items: center; font-size: 1.25rem; font-weight: bold; color: black;">
            <span>Preview ({{ payments.length }} payments)</span>
            <div style="display: flex; gap: 1rem;">
              <span :class="{'btn-outline button-primary' : payments.filter(item => item.status == 1).length > 0, 'btn-outline-light btn':  payments.filter(item => item.status == 1).length == 0}" :style="{cursor: 'default', 'pointer-events': 'none', 'background-color': payments.filter(item => item.status == 1).length > 0 ? '#f0fdf4':'white', color: payments.filter(item => item.status == 1).length > 0 ? '#008236':''}">{{ payments.filter(item => item.status == 1).length }} valid</span>

              <span v-if="payments.filter(item => item.status == 0).length > 0" class="btn-outline button-primary" style="cursor: default; pointer-events: none; background-color: #fef2f2; color: #c10007; border-color: #ffc9c9 !important">{{ payments.filter(item => item.status == 0).length }} invalid</span>

              <button class="btn-outline-light btn" @click="payments = []"><i class="fa-regular fa-xmark"></i> Clear</button>
            </div>
          </div>
          <b-table responsive
            class="table-header-border"
            style="margin-bottom:0;"
            :items="payments"
            :fields="fields"
            primary-key="id"
          >

          <template #head(index)>
            <span>#</span>
          </template>

          <template #cell(status)="data">
            <i style="color: #00a63e;" v-if="data.item.status == 1" class="fa-regular fa-circle-check"></i><i style="color: #d4183d;" v-else class="fa-regular fa-circle-exclamation"></i>
          </template>

          <template #cell(VIBAN)="data">
            <span>
              {{ data.item.VIBAN }}
            </span>
          </template>

          <template #cell(amount)="data">
            <span>
              €{{ data.item.amount }}
            </span>
          </template>

          <template #cell(date)="data">
            <span>
              {{ data.item.date }}
            </span>
          </template>

          <template #cell(reference)="data">
            <span>
              {{ data.item.reference }}
            </span>
          </template>

          </b-table>
          <div class="card p-6" v-if="payments.some(item => item.status == 1)"  style="color: #d4183d;">
            <span style="display: flex; gap: 1rem; font-weight: bold;"><i class="fa-regular fa-circle-exclamation"></i> Validation Errors Found:</span>
            <span v-for="(item, index) in payments.filter(p => p.status === 0)" :key="index">
              Row {{ index + 2 }}: VIBAN not in pool – payment will be unidentified
            </span>
          </div>
          <hr>
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <button class="btn-outline-light btn" @click="closeModal">Cancel</button>
            <button v-if="payments.filter(item => item.status == 1).length > 0" class="btn-outline btn-dark" @click="addToQueue(payments.filter(item => item.status == 1))">Add {{ payments.filter(item => item.status == 1).length }} Valid Payments to Queue</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import alertModal from './alertModal.vue';
export default {
  components: { alertModal },
  name: 'ImportPaymentModal',
  emits: ['update:payments'],
  data(){
    return {
      fields: [
        { key: 'index', thClass: 'text-center align-middle border-bottom', tdClass: 'text-center align-middle'},
        { key: 'status', label: 'Status', thClass: 'text-left align-middle border-bottom', tdClass: 'align-middle text-left'},
        { key: 'VIBAN', label: 'VIBAN', thClass: 'text-left align-middle border-bottom', tdClass: 'align-middle text-left text-nowrap'},
        { key: 'amount', label: 'Amount', thClass: 'text-right align-middle border-bottom', tdClass: 'align-middle text-left text-nowrap'},
        { key: 'reference', label: 'Reference', thClass: 'text-left align-middle border-bottom', tdClass: 'align-middle text-left text-nowrap'},
        { key: 'date', label: 'Date', thClass: 'text-left align-middle border-bottom', tdClass: 'align-middle text-left text-nowrap'}
      ],
      isModalOpen: false,
      isAlertOpen: false,
      countCreated: 0,
      countExisting: 0,
      payments : [],
      isDragOver: false
    }
  },
  mounted() {
    window.addEventListener('dragover', e => e.preventDefault())
    window.addEventListener('drop', e => e.preventDefault())
  },
  methods: {
    openModal (){
      this.isModalOpen = true
    },
    closeModal (){
      this.isModalOpen = false
      this.countExisting = 0
      this.countCreated = 0
      this.payments = []
    },
    exportCSVTemplate (){
      let firstLine = 'viban ; amount ; date ; reference' + '\r\n'

      let line = ''
      line += 'CLI-FR1420041010050500013M02606 ; 100.00 ; 2026-01-12 ; PAYMENT-001'  + '\r\n'
      line += 'DE89370400440532013000 ; 250.50 ; 2026-01-12 ; PAYMENT-002' + '\r\n'
      line += 'GB29NWBK60161331926819 ; 500.00 ; 2026-01-12 ; PAYMENT-003' + '\r\n'

      let data = firstLine + line
      let linkSource = 'data:text/csv;charset:utf-8;base64,' + btoa(data)
      let downloadLink = document.createElement('a')
      let today = moment()
      let fileName = 'import_CSV_template_' + today.unix() + '.csv'

      downloadLink.href = linkSource
      downloadLink.download = fileName
      downloadLink.click()
    },
    onDrop(event) {
      this.isDragOver = false
      const file = event.dataTransfer.files[0]
      this.readCSVFile(file)
    },
    readCSVFile(file) {
      if (!file || !file.name.endsWith('.csv')) return
      const reader = new FileReader()
      reader.onload = (e) => {
        const csvText = e.target.result
        const fileContent = csvText
          .trim()
          .split('\r\n')
          .filter(item => item && item !== 'viban ; amount ; date ; reference')

        fileContent.forEach(el => {
          let elements = el.split(';')
          let VIBAN = elements[0]
          let amount = elements[1]
          let date = elements[2]
          let reference = elements[3]
          let data = {
            VIBAN: VIBAN,
            amount: amount,
            date: date,
            reference: reference
          }
          this.checkAllocation(data)
        })
      }
      reader.readAsText(file)
    },
    checkAllocation(data){
      const sanitized = this.sanitize(data.VIBAN)
      if (!sanitized) return

      this.$engine.services.payments.getGroups({ search : sanitized , maxResults : 200})
      .then(res => {
        if(res.data.references.length == 0){
          this.payments.push({
            VIBAN: data.VIBAN,
            amount: data.amount,
            date: new Date().toLocaleDateString(),
            reference: data.reference,
            status: 0
          })
        } else{
          this.payments.push({
            amount: data.amount,
            date: new Date().toLocaleDateString(),
            reference: data.reference,
            VIBAN: data.VIBAN,
            status: 1
          })
        }
      })
      .catch(err => console.log(err))
    },
    importCSVFile() {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = '.csv'

      input.addEventListener('change', (event) => {
        const file = event.target.files[0]
        this.readCSVFile(file)
      })

      input.click()
    },
    addToQueue(data){
      this.$emit('update:payments', data)
      this.closeModal()
    },
    sanitize(value) {
      if (typeof value !== 'string') return ''

      return value
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9-_]/g, '')
        .replace(/[\u200B-\u200D\uFEFF]/g, '')
        .replace(/\r?\n|\r/g, '')
    }
  }
}
</script>
<style scoped>

.drop-zone.hover {
  border-color: #155dfc;
  background-color: #eff6ff;
}

.download-btn{
  text-wrap: nowrap;
  background-color: #fff !important;
  border-color: #8ec5ff !important;
  display: flex !important;
  gap: 1rem;
}

.drop-zone{
  row-gap: 1rem;
  border: 2px dashed #d1d5dc;
  background-color: #f8f8f8;
  border-radius: 0.625rem;
  padding: calc(.25rem * 12);
  display: grid !important;
  justify-content: center !important;
  align-items: center !important;
}

.drop-zone>i{
  display: flex;
  justify-content: center;
  font-size: calc(.25rem * 12);
  font-weight: bold;
  color: #99a1af;
  margin: 1rem 0rem;
}

.drop-zone>span{
  display: flex;
  justify-content: center;
}

.drop-zone:hover{
  border-color: #99a1af;
}

.drop-zone>div{
  display: flex;
  justify-content: center;
}

.information-container{
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: calc(.25rem * 4);
  background-color: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: .625rem;
  margin: 1rem 0rem;
}

.information-container>i {
  color: #155dfc;
  font-size: calc(.25rem * 5);
}

.information-container>div{
  display: grid;
}

.information-container>div>span:first-child{
  color: #1c398e;
  font-weight: bold;
}

.information-container>div>span:last-child{
  color: #1d4ed8;
}

.modal-container{
  width: 100vw;
  height: 100vh;
  position: fixed;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 5000;
  overflow: hidden;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-content .filter-item {
  margin: 1rem 0rem;
}

.modal-content>h1{
  font-weight: bold;
  font-size: 1.3rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #000;
}

.modal-content>h1>i{
  cursor: pointer;
}

.modal-content>input{
  margin-bottom: 1.5rem;
}

.modal-content>span:first-of-type{
  color: #717182;
  margin-bottom: 1rem;
}

.modal-content>span:last-of-type{
  color: #000;
  font-weight: bold;
}
</style>